import { Icon } from '@iconify/react'
import { useCallback, useEffect, useState } from 'react'
import { getCollections, getPresets, runBuilderQuery, runRawQuery, savePreset as apiSavePreset, deletePreset as apiDeletePreset } from '../api'
import type { CollectionSchema, Preset, BuilderFilter } from '../api'
import { ExportPanel } from '../components/ExportPanel'

export function QueryConsole() {
  const [rawQuery, setRawQuery] = useState('SELECT * FROM github_repository_search_results LIMIT 10;')
  const [rawOutput, setRawOutput] = useState('')
  const [builderOutput, setBuilderOutput] = useState('')

  const [rawResult, setRawResult] = useState<unknown>(null)
  const [builderResult, setBuilderResult] = useState<unknown>(null)
  const [rawTemplate, setRawTemplate] = useState('data.map(x => JSON.stringify(x)).join("\\n")')
  const [builderTemplate, setBuilderTemplate] = useState('data.map(x => JSON.stringify(x)).join("\\n")')
  const [exportError, setExportError] = useState('')

  const [presets, setPresets] = useState<Preset[]>([])
  const [presetsLoading, setPresetsLoading] = useState(true)
  const [selectedPreset, setSelectedPreset] = useState('')

  const [collections, setCollections] = useState<CollectionSchema[]>([])
  const [collectionsLoading, setCollectionsLoading] = useState(true)
  const [collectionsError, setCollectionsError] = useState('')

  const [builderCollection, setBuilderCollection] = useState('')
  const [builderFields, setBuilderFields] = useState<string[]>([])
  const [builderFilters, setBuilderFilters] = useState<BuilderFilter[]>([])
  const [builderLimit, setBuilderLimit] = useState(100)

  useEffect(() => {
    let cancelled = false
    setCollectionsLoading(true)
    getCollections()
      .then((cols) => {
        if (cancelled) return
        setCollections(cols)
        if (cols.length > 0) {
          setBuilderCollection(cols[0].name)
          setBuilderFields(cols[0].fields.slice(0, Math.min(4, cols[0].fields.length)))
          setRawQuery(`SELECT * FROM ${cols[0].name} LIMIT 10;`)
        } else {
          setRawQuery('-- No collections yet. Ingest data first, or use raw query after data exists.')
        }
      })
      .catch((e) => {
        if (!cancelled) setCollectionsError(e instanceof Error ? e.message : 'fetch failed')
      })
      .finally(() => {
        if (!cancelled) setCollectionsLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false
    setPresetsLoading(true)
    getPresets()
      .then((list) => {
        if (!cancelled) setPresets(list)
      })
      .catch(() => {
        if (!cancelled) setPresets([])
      })
      .finally(() => {
        if (!cancelled) setPresetsLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  const collectionMap = Object.fromEntries(collections.map((c) => [c.name, c]))
  const availableFields = collectionMap[builderCollection]?.fields ?? []

  const onRunRaw = async () => {
    setRawOutput('running...')
    setExportError('')
    try {
      const out = await runRawQuery(rawQuery)
      setRawResult(out)
      setRawOutput(JSON.stringify(out, null, 2))
    } catch (e) {
      setRawResult(null)
      setRawOutput(e instanceof Error ? e.message : 'query failed')
    }
  }

  const buildSurrealQL = useCallback((collection: string, fields: string[], filters: BuilderFilter[], limit: number) => {
    const fieldList = fields.length > 0 ? fields.join(', ') : '*'
    let q = `SELECT ${fieldList} FROM ${collection}`
    const ciOps = new Set(['CONTAINS', 'NOT CONTAINS', 'LIKE'])
    const whereClauses = filters
      .filter((f) => f.field && f.op && f.value)
      .map((f) => {
        // Map frontend op names to SurrealQL operators
        const opMap: Record<string, string> = {
          '=': '=',
          '!=': '!=',
          '>': '>',
          '<': '<',
          '>=': '>=',
          '<=': '<=',
          'CONTAINS': 'CONTAINS',
          'NOT CONTAINS': 'CONTAINSNOT',
          'LIKE': 'LIKE',
          '~': '~',
        }
        const surrealOp = opMap[f.op] ?? f.op
        // Quote string values, leave numbers/bools bare
        const v = f.value
        const val = (v === 'true' || v === 'false') ? v
          : Number.isNaN(Number(v)) || v.trim() === '' ? `'${v.replace(/'/g, "\\'")}'`
          : v
        let clause = `${f.field} ${surrealOp} ${val}`
        // Case-insensitive wrapping for text search ops
        if (ciOps.has(f.op)) {
          clause = `string::lowercase(${f.field}) ${surrealOp} string::lowercase(${val})`
        }
        return clause
      })
    if (whereClauses.length > 0) {
      q += ' WHERE ' + whereClauses.join(' AND ')
    }
    q += ` LIMIT ${limit};`
    return q
  }, [])

  const onRunBuilder = async () => {
    setBuilderOutput('running...')
    setExportError('')
    const activeFilters = builderFilters.filter((f) => f.field && f.op && f.value)
    try {
      const out = await runBuilderQuery({
        collection: builderCollection,
        fields: builderFields,
        filters: activeFilters,
        limit: builderLimit,
      })
      setBuilderResult(out)
      setBuilderOutput(JSON.stringify(out, null, 2))
      // Reflect built query to raw query textarea
      const builtQL = buildSurrealQL(builderCollection, builderFields, activeFilters, builderLimit)
      setRawQuery(builtQL)
    } catch (e) {
      setBuilderResult(null)
      setBuilderOutput(e instanceof Error ? e.message : 'query failed')
    }
  }

  const addFilter = () => {
    setBuilderFilters((prev) => [...prev, { field: '', op: '=', value: '' }])
  }

  const updateFilter = (idx: number, patch: Partial<BuilderFilter>) => {
    setBuilderFilters((prev) => {
      const next = [...prev]
      next[idx] = { ...next[idx], ...patch }
      return next
    })
  }

  const removeFilter = (idx: number) => {
    setBuilderFilters((prev) => prev.filter((_, i) => i !== idx))
  }

  const savePreset = async () => {
    const name = window.prompt('Preset name:')
    if (!name?.trim()) return
    const preset: Preset = {
      name: name.trim(),
      rawQuery,
      rawTemplate,
      builderTemplate,
      builderCollection,
      builderFields,
      builderFilters,
      builderLimit,
    }
    try {
      await apiSavePreset(preset)
      setPresets((prev) => {
        const next = [...prev.filter((p) => p.name !== preset.name), preset]
        return next
      })
      setSelectedPreset(preset.name)
    } catch (e) {
      setExportError(e instanceof Error ? e.message : 'save failed')
    }
  }

  const loadPreset = (name: string) => {
    const preset = presets.find((p) => p.name === name)
    if (!preset) return
    setRawQuery(preset.rawQuery)
    setRawTemplate(preset.rawTemplate)
    setBuilderTemplate(preset.builderTemplate)
    setBuilderCollection(preset.builderCollection)
    setBuilderFields(preset.builderFields)
    setBuilderFilters(preset.builderFilters)
    setBuilderLimit(preset.builderLimit)
    setRawOutput('')
    setBuilderOutput('')
    setRawResult(null)
    setBuilderResult(null)
    setExportError('')
  }

  const deletePreset = async () => {
    if (!selectedPreset) return
    try {
      await apiDeletePreset(selectedPreset)
      setPresets((prev) => prev.filter((p) => p.name !== selectedPreset))
      setSelectedPreset('')
    } catch (e) {
      setExportError(e instanceof Error ? e.message : 'delete failed')
    }
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <Icon icon="mdi:code-tags" width="22" height="22" className="text-cyan-400" />
        <h1 className="text-xl font-semibold">Query console</h1>
      </div>

      <div className="mb-4 flex items-center gap-2 border-b border-slate-700 pb-3">
        <Icon icon="mdi:bookmark-outline" width="18" height="18" className="text-slate-400 shrink-0" />
        <select
          className="field w-48 text-sm"
          value={selectedPreset}
          onChange={(e) => {
            setSelectedPreset(e.target.value)
            if (e.target.value) loadPreset(e.target.value)
          }}
        >
          <option value="">Load preset...</option>
          {presets.map((p) => (
            <option key={p.name} value={p.name}>{p.name}</option>
          ))}
        </select>
        <button className="btn text-xs" onClick={savePreset}>Save current</button>
        <button
          className="rounded-md bg-slate-700 px-2 py-1.5 text-xs text-slate-300 hover:bg-rose-700 hover:text-white disabled:opacity-40"
          disabled={!selectedPreset}
          onClick={deletePreset}
        >
          Delete
        </button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Left column — row 1: Raw query */}
        <div className="panel">
          <h2 className="mb-3 text-lg font-semibold">
            Raw query (read-only)
            <a
              href="https://surrealdb.com/docs/learn/querying/surrealql/statements-and-values"
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 inline-block align-middle text-slate-500 hover:text-cyan-400 transition-colors"
              title="SurrealDB query reference"
            >
              <Icon icon="mdi:information-outline" width="18" height="18" />
            </a>
          </h2>
          <textarea
            className="field h-36"
            value={rawQuery}
            onChange={(e) => setRawQuery(e.target.value)}
          />
          <button className="btn mt-3" onClick={() => void onRunRaw()}>Run raw query</button>
          <pre className="output mt-3">{rawOutput || 'No query run yet.'}</pre>
        </div>

        {/* Right column — row 1: Collections */}
        <div className="panel">
          <h2 className="mb-3 text-lg font-semibold">Collections</h2>
          {collectionsLoading && <p className="text-sm text-slate-400">Loading...</p>}
          {collectionsError && <p className="text-sm text-rose-300">Error: {collectionsError}</p>}
          {!collectionsLoading && !collectionsError && collections.length === 0 && (
            <p className="text-sm text-slate-400">No collections found. Ingest data first.</p>
          )}
          <div className="grid gap-2 text-sm text-slate-300">
            {collections.map((col) => (
              <div key={col.name} className="rounded border border-slate-700/60 bg-slate-900/60 px-3 py-2">
                <div className="font-medium text-slate-100">{col.label}</div>
                <div className="text-xs text-slate-400">{col.description}</div>
                <div className="mt-1 font-mono text-xs text-slate-500">{col.name}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Left column — row 2: Builder query */}
        <div className="panel">
          <h2 className="mb-3 text-lg font-semibold">Builder query</h2>
          <label className="mb-2 block text-sm">Collection</label>
          <select
            className="field mb-3"
            value={builderCollection}
            onChange={(e) => {
              const nextCollection = e.target.value
              setBuilderCollection(nextCollection)
              const nextFields = collectionMap[nextCollection]?.fields ?? []
              setBuilderFields(nextFields.slice(0, Math.min(4, nextFields.length)))
            }}
          >
            {collections.map((col) => (
              <option key={col.name} value={col.name}>{col.label}</option>
            ))}
          </select>

          <label className="mb-2 block text-sm">Fields (multi-select)</label>
          <select
            className="field mb-3 h-36"
            multiple
            value={builderFields}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions).map((opt) => opt.value)
              setBuilderFields(selected)
            }}
          >
            {availableFields.map((field) => (
              <option key={field} value={field}>{field}</option>
            ))}
          </select>
          <label className="mb-2 block text-sm">Limit</label>
          <input
            className="field mb-3"
            type="number"
            min={1}
            max={5000}
            value={builderLimit}
            onChange={(e) => setBuilderLimit(Math.max(1, Number(e.target.value) || 1))}
          />

          <div className="mb-3">
            <div className="mb-1 flex items-center justify-between">
              <label className="text-sm">Filters</label>
              <button className="text-xs text-cyan-400 hover:text-cyan-300" onClick={addFilter}>+ Add filter</button>
            </div>
            <datalist id="filter-field-suggestions">
              {availableFields.map((f) => (
                <option key={f} value={f} />
              ))}
            </datalist>
            {builderFilters.map((f, i) => (
              <div key={i} className="mb-1.5 flex gap-1 items-start">
                <input
                  className="field flex-[2]"
                  placeholder="field (e.g. attributes.raw_result.archived)"
                  list="filter-field-suggestions"
                  value={f.field}
                  onChange={(e) => updateFilter(i, { field: e.target.value })}
                />
                <select
                  className="field flex-1"
                  value={f.op}
                  onChange={(e) => updateFilter(i, { op: e.target.value })}
                >
                  <option value="=">=</option>
                  <option value="!=">!=</option>
                  <option value=">">&gt;</option>
                  <option value="<">&lt;</option>
                  <option value=">=">&gt;=</option>
                  <option value="<=">&lt;=</option>
                  <option value="CONTAINS">CONTAINS (text in)</option>
                  <option value="NOT CONTAINS">NOT CONTAINS</option>
                  <option value="LIKE">LIKE</option>
                  <option value="~">~ (regex)</option>
                </select>
                <input
                  className="field flex-[2]"
                  placeholder="value"
                  value={f.value}
                  onChange={(e) => updateFilter(i, { value: e.target.value })}
                />
                <button className="text-xs text-rose-400 hover:text-rose-300 px-1 py-1.5" onClick={() => removeFilter(i)} title="Remove filter">
                  <Icon icon="mdi:close" width="16" height="16" />
                </button>
              </div>
            ))}
          </div>

          <button className="btn" onClick={() => void onRunBuilder()}>Run builder query</button>
          <pre className="output mt-3">{builderOutput || 'No query run yet.'}</pre>
        </div>

        {/* Right column — row 2: Export section (shared) */}
        <div className="panel">
          <h2 className="mb-3 text-lg font-semibold">
            <Icon icon="mdi:export" width="18" height="18" className="mr-1 inline text-cyan-400" />
            Export
          </h2>

          {rawResult == null && builderResult == null && (
            <p className="text-sm text-slate-400">Run a query to enable export.</p>
          )}

          {rawResult != null && (
            <div className={builderResult != null ? 'mb-6' : ''}>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Raw query export</h3>
              <ExportPanel
                data={rawResult}
                template={rawTemplate}
                onTemplateChange={setRawTemplate}
                initialEngine="lodash"
                fileName="raw-export.txt"
              />
            </div>
          )}

          {builderResult != null && (
            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Builder export</h3>
              <ExportPanel
                data={builderResult}
                template={builderTemplate}
                onTemplateChange={setBuilderTemplate}
                initialEngine="lodash"
                fileName="builder-export.txt"
              />
            </div>
          )}
        </div>
      </div>
      {exportError && <p className="mt-4 text-xs text-rose-300">{exportError}</p>}
    </div>
  )
}
